import { useMemo, useState } from 'react';
import type { AxonEvent } from '@reflex/shared';
import { AnsiText } from '@reflex/ui/components/content/AnsiText';
import { EventDetailTooltip } from '@reflex/ui/components/stream/EventMeta';
import { Button } from '@reflex/ui/components/ui/button';
import { cn } from '@reflex/ui/lib/utils';
import { ChevronDown, Terminal } from 'lucide-react';
import type { AgentLogBlock, AgentLogLine } from '../types.js';
import { AnimatedCollapse } from './AnimatedCollapse.js';
import { trimEmptyLogEdges } from './agent-log-lines.js';

/**
 * A run of consecutive stdout/stderr lines as one grouped, collapsible console
 * card. Grouping keeps noisy broker output (e.g. repeated "config not found"
 * warnings) visually contained and clearly labelled, rather than scattering
 * individual `>_` lines across the transcript.
 *
 * Shared by both places a log run can land: its own timeline row between turns
 * and an {@link AgentLogBlock} inside one.
 */
export function AgentLogConsole({
  lines,
  timestamp,
  sourceEvent,
  dataEventId,
  className,
}: {
  lines: readonly AgentLogLine[];
  /** Time of the newest line, shown on the header row. */
  timestamp: number;
  /** Event behind the newest line, for the hover detail tooltip. */
  sourceEvent?: AxonEvent;
  /** Event the card anchors to for deep-link highlighting — the run's first. */
  dataEventId?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(true);
  const visibleLines = useMemo(() => trimEmptyLogEdges(lines), [lines]);
  const lineCount = visibleLines.length;
  const time = new Date(timestamp).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  if (lineCount === 0) return null;

  return (
    <div
      className={cn(
        'group/log overflow-hidden rounded-md border border-border/60 bg-muted/20',
        className,
      )}
      data-testid="agent-log-console"
      data-event-id={dataEventId}
    >
      <div className="flex items-center">
        <Button
          variant="ghost"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="h-auto flex-1 justify-start gap-1.5 rounded-none px-3 py-1.5 text-2xs font-normal text-muted-foreground hover:bg-transparent hover:text-foreground dark:hover:bg-transparent"
        >
          <Terminal className="size-3 shrink-0 text-muted-foreground/60" />
          <span className="font-medium">Agent Logs</span>
          <span className="text-muted-foreground/60">
            {lineCount} {lineCount === 1 ? 'line' : 'lines'}
          </span>
          <span className="ml-auto tabular-nums text-muted-foreground/60">{time}</span>
          <ChevronDown
            className={cn(
              'size-3 shrink-0 text-muted-foreground/60 transition-transform',
              !open && '-rotate-90',
            )}
          />
        </Button>
        {sourceEvent && (
          <div className="px-2 opacity-0 transition-opacity group-hover/log:opacity-100">
            <EventDetailTooltip event={sourceEvent} />
          </div>
        )}
      </div>
      <AnimatedCollapse open={open}>
        <div className="border-t border-border/40 px-3 py-2 font-mono text-2xs leading-relaxed text-muted-foreground/80">
          {visibleLines.map((line) =>
            line.text.trim().length === 0 ? (
              <div key={line.id} className="h-3" aria-hidden />
            ) : (
              // Each row carries its own event id: a deep link from the event
              // stream names one line, and the run's card can't stand in for it.
              <div
                key={line.id}
                className="whitespace-pre-wrap break-words"
                data-event-id={line.sourceEvent?.id}
              >
                <AnsiText text={line.text} />
              </div>
            ),
          )}
        </div>
      </AnimatedCollapse>
    </div>
  );
}

/**
 * In-turn log run, sized to sit among the turn's tool rows. `TurnBlockRenderer`
 * already wraps the block in its own `data-event-id`, so this passes none.
 */
export function AgentLogBlockView({ block }: { block: AgentLogBlock }) {
  return (
    <AgentLogConsole
      lines={block.lines}
      timestamp={block.timestamp}
      sourceEvent={block.lines[block.lines.length - 1]?.sourceEvent ?? block.sourceEvent}
      className="my-1"
    />
  );
}
